/*
 * Main and demo navigation arrays
 *
 * 'to' attribute points to the route name, not the path url
 *
 * Section headings use heading: true with a sub array containing their items.
 * The 'roles' array on a heading controls which roles can see that section.
 * The 'roles' array on an item controls which roles can see that item within the section.
 * Items or sections with no 'roles' are visible to all authenticated users.
 *
 * NOTE: Role-based menu gating is deferred until backend role taxonomy
 * (owner/admin/user) is aligned with menu role labels.
 */

import type { RouteLocation } from 'vue-router';

export type BadgeVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
export type MenuItem = {
    badge?: string;
    'badge-variant'?: BadgeVariant;
    'badge-to'?: string | RouteLocation;
    name: string;
    to?: string | RouteLocation;
    icon?: string;
    heading?: boolean;
    roles?: string[];
    subActivePaths?: string;
    open?: boolean;
    sub?: MenuItem[];
    target?: string;
};

export interface Menu {
    main: MenuItem[];
    demo?: MenuItem[];
}

const menu = {
    main: [
        {
            name: 'Dashboard',
            to: 'dashboard',
            icon: 'material-symbols:dashboard',
        },
        {
            name: 'Marketing',
            heading: true,
            sub: [
                {
                    name: 'Map',
                    to: 'dashboard.map',
                    icon: 'material-symbols:map',
                },
                {
                    name: 'Trends',
                    to: 'dashboard.trends',
                    icon: 'material-symbols:insights',
                },
                {
                    name: 'Advertising',
                    to: 'advertising',
                    icon: 'material-symbols:campaign',
                },
                {
                    name: 'Coupons',
                    to: 'marketing.coupons',
                    icon: 'material-symbols:confirmation-number',
                },
                {
                    name: 'Shipments',
                    to: 'shipments',
                    icon: 'material-symbols:local-shipping',
                },
            ],
        },
        {
            name: 'Inventory',
            heading: true,
            sub: [
                {
                    name: 'Products',
                    to: 'products',
                    icon: 'material-symbols:package-2',
                },
                {
                    name: 'SKUs',
                    to: 'skus',
                    icon: 'material-symbols:barcode-scanner',
                },
                {
                    name: 'Stock',
                    to: 'stocks',
                    icon: 'material-symbols:inventory-2',
                },
                {
                    name: 'Purchase Orders',
                    to: 'purchase-orders',
                    icon: 'material-symbols:receipt-long',
                },
                {
                    name: 'Warehouse Transfers',
                    to: 'transfers',
                    icon: 'material-symbols:transfer-within-a-station',
                    subActivePaths: 'transfers',
                    open: true,
                    'badge-to': {
                        name: 'transfers',
                        query: {
                            draft: 'true',
                            live: 'true',
                            completed: 'true',
                            problems: true,
                        },
                    },
                    sub: [
                        {
                            name: 'Upcoming Transfers',
                            to: {
                                name: 'transfers',
                                query: {
                                    draft: 'true',
                                    live: 'false',
                                    completed: 'false',
                                    sort: 'planned_shipping_date',
                                    sortDesc: 'false',
                                },
                            },
                            icon: 'material-symbols:hourglass-empty',
                        },
                    ],
                },
                {
                    name: 'Suppliers',
                    to: 'suppliers',
                    icon: 'material-symbols:handshake',
                },
                {
                    name: 'Warehouses',
                    to: 'warehouses',
                    icon: 'material-symbols:warehouse',
                },
                {
                    name: 'Forecasts',
                    to: 'forecasts',
                    icon: 'material-symbols:show-chart',
                },
                {
                    name: 'Analysis',
                    to: 'stocks-analysis',
                    icon: 'material-symbols:pie-chart',
                },
            ],
        },
        {
            name: 'Customer Support',
            heading: true,
            to: 'customer-support',
            sub: [
                {
                    name: 'Orders',
                    to: 'store-orders',
                    icon: 'material-symbols:receipt',
                    'badge-to': {
                        name: 'store-orders',
                        query: {
                            problems: true,
                        },
                    },
                },
                {
                    name: 'Subscriptions',
                    to: 'subscriptions',
                    icon: 'material-symbols:repeat',
                },
                {
                    name: 'Customers',
                    to: 'customers',
                    icon: 'material-symbols:person',
                },
                {
                    name: 'Stores',
                    to: 'stores',
                    icon: 'material-symbols:storefront',
                },
                {
                    name: 'Disputes',
                    to: 'disputes',
                    icon: 'material-symbols:balance',
                },
            ],
        },
        {
            name: 'Accounting',
            heading: true,
            sub: [
                {
                    name: 'VAT Reports',
                    to: 'accounting.vat-reports',
                    icon: 'material-symbols:csv',
                },
                {
                    name: 'Cross Border VAT',
                    to: 'accounting.cross-border-vat',
                    icon: 'material-symbols:summarize',
                },
                {
                    name: 'Inventory By Date',
                    to: 'inventory-by-date',
                    icon: 'material-symbols:calendar-month',
                },
                {
                    name: 'Profit & Loss',
                    to: 'accounting.profit-loss',
                    icon: 'material-symbols:trending-up',
                },
            ],
        },
    ],
};

export default menu as Menu;
